import React, { useState, useEffect, useMemo, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar, faCaretDown, faCaretUp, faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { APIProvider } from "@vis.gl/react-google-maps";
import CustomMap from "../components/CustomMap";
import "./../styles/Detail.css";
import { AuthContext } from "../contexts/AuthContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Detail = () => {
	const location = useLocation();
	var tempData = location.state; 	
	if (!tempData) {
		const results = JSON.parse(localStorage.getItem("results"));
		const index = parseInt(window.location.href.split('/')[4]);
		tempData = results[index];
	}
	const data = tempData;

	const { zipCode, countyName } = useContext(AuthContext);
	const navigate = useNavigate();
	const [showAllAddresses, setShowAllAddresses] = useState(false);
	const [showAllPhones, setShowAllPhones] = useState(false);
	const [showAllEmails, setShowAllEmails] = useState(false);

	const [markerLocation, setMarkerLocation] = useState(null);
	const [currentAddressIndex, setCurrentAddressIndex] = useState(0);
	const [showMap, setShowMap] = useState(true);


	// Function to make URLs in text clickable
	const makeTextClickable = (text) => {
		if (!text) return text;
		const urlPattern = /(?:\()?(https?:\/\/[^\s]+|www\.[^\s]+|[^\s]+\.(com|org|net|gov|edu|info))(?:\))?/gi;
		return text.replace(urlPattern, (match) => {
			const cleanedMatch = match.replace(/[()]/g, "");
			const href = cleanedMatch.startsWith("http") ? cleanedMatch : `http://${cleanedMatch}`;
			return `<a href="${href}" target="_blank" rel="noopener noreferrer">${cleanedMatch}</a>`;
		});
	};

	const createMarkup = (text) => {
		return { __html: makeTextClickable(text) };
	};

	// More flexible regex pattern for addresses
	const isValidAddress = (address) => {
		const addressPattern = /^\d+\s[A-Za-z0-9.,'\s-]+(?:,\s*[A-Za-z\s]+)?,?\s*[A-Za-z]{2}\s*\d{5}(?:-\d{4})?$/;
		return addressPattern.test(address);
	};

	//Checks Phone format. Depend if we make phone links
	const isValidPhone = (phone) => {
		// const phonePattern = ;
		// return phonePattern.test(phone);
	};

	const getGoogleMapsLink = (address) => {
		return isValidAddress(address) ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}` : null;
	};

	const fetchCoordinates = async (address) => {
		const encodedAddress = encodeURIComponent(address);
		const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodedAddress}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;

		try {
			const response = await fetch(geocodeUrl);
			const data = await response.json();

			if (data.results.length > 0) {
				const location = data.results[0].geometry.location;
				setMarkerLocation({ lat: location.lat, lng: location.lng });
				setShowMap(true);
			} else {
				console.error("Geocoding failed: No results found.");
				setShowMap(false);
			}
		} catch (error) {
			console.error("Error fetching geocode");
			setShowMap(false);
		}
	};

	const handlePrevious = () => {
		if (currentAddressIndex > 0) {
			setCurrentAddressIndex(currentAddressIndex - 1);
		}
	};

	const handleNext = () => {
		if (currentAddressIndex < sortedAddresses.length - 1) {
			setCurrentAddressIndex(currentAddressIndex + 1);
		}
	};

	const renderStars = (rating) => {
		const stars = [];
		for (let i = 1; i <= 5; i++) {
			stars.push(<FontAwesomeIcon key={i} icon={faStar} style={{ color: i <= rating ? "#007B55" : "#ddd", fontSize: "16px", marginRight: "4px" }} />);
		}
		return stars;
	};

	const isValidField = (field) => {
		return field && field.toLowerCase() !== "n/a" && field.toLowerCase() !== "na";
	};

	const sortedAddresses = useMemo(() => (Array.isArray(data.Address) ? data.Address : []), [data.Address]);
	const sortedPhones = useMemo(() => (Array.isArray(data.Phone) ? data.Phone : []), [data.Phone]);
	const sortedEmails = useMemo(() => (Array.isArray(data.Email) ? data.Email : []), [data.Email]);

	const getClosestAddress = async () => {
		if (data.Coordinates.length < 2) return;

		// Get coordinates for User's zip code or county name
		const googleMapsApiKey = process.env.GOOGLE_MAPS_API_KEY; // Replace with your Google Maps API key
		let geocodeUrl = '';
		if (zipCode != null) {
			geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?address=${zipCode}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;
		} else if(countyName != null) {
			geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?address=${countyName}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;
		}
		
		try {
			const geocodeResponse = await fetch(geocodeUrl);
			const geocodeData = await geocodeResponse.json();
			const location = geocodeData.results[0].geometry.location;
			console.log(geocodeResponse);
			const coordinates = [location.lng, location.lat];

			// Calculate the closest address
			let distances = [];

			data.Coordinates.forEach((pair, index) => {
				const distance = Math.sqrt(Math.pow(pair.coordinates[0] - coordinates[0], 2) + Math.pow(pair.coordinates[1] - coordinates[1], 2));
				distances.push({ index: index, distance: distance });
			});

			distances.sort((a, b) => a.distance - b.distance);
			setCurrentAddressIndex(distances[0].index);
			return;
		} catch (error) {
			console.log("Error: ", error);
			toast.error("Unable To Load Map Data");
		}
	}

	useEffect(() => {
		if (sortedAddresses.length > 0 && sortedAddresses[currentAddressIndex]) {
			fetchCoordinates(sortedAddresses[currentAddressIndex]);
		}
	}, [currentAddressIndex, sortedAddresses, setCurrentAddressIndex]);

	useEffect(() => {
		getClosestAddress();
	}, []);

	const toggleAddressVisibility = () => {
		setShowAllAddresses(!showAllAddresses);
	};

  	const togglePhoneVisibility = () => {
		setShowAllPhones(!showAllPhones);
	};

  	const toggleEmailVisibility = () => {
		setShowAllEmails(!showAllEmails);
	};



	return (
		<div className="detail-container">
			<h1>{data.Name}</h1>
			<p>
				<strong>County:</strong> {data.County}
			</p>
			<p>
				<strong>Services:</strong> <span dangerouslySetInnerHTML={createMarkup(data.Services)} />
			</p>
			<p>
				<strong>Eligibility Requirements:</strong> {data.Eligibility_Requirement}
			</p>
			<p>
				<strong>How to Apply:</strong> <span dangerouslySetInnerHTML={createMarkup(data.How_To_Apply)} />
			</p>

			{sortedPhones.length > 0 && (
				<p>
					<strong>Phone: </strong>
					<span>{sortedPhones[currentAddressIndex]}</span>
				</p>
			)}

			{sortedPhones.length > 1 && (
				<div>
					<button onClick={togglePhoneVisibility} className="phone-toggle-button">
						{showAllPhones ? "Show Less" : "Show More Phone Numbers"}
						<FontAwesomeIcon icon={showAllPhones ? faCaretUp : faCaretDown} />
					</button>
					{showAllPhones && (
						<ul className="additional-phones">
							{sortedPhones.slice(1).map((phone, index) => (
								<li key={index}>
									<span>{phone}</span>
								</li>
							))}
						</ul>
					)}
				</div>
			)}

			{sortedEmails.length > 0 && (
				<p>
					<strong>Email: </strong>
					<span>{sortedEmails[currentAddressIndex]}</span>
				</p>
			)}

			{sortedEmails.length > 1 && (
				<div>
					<button onClick={toggleEmailVisibility} className="email-toggle-button">
						{showAllEmails ? "Show Less" : "Show More Emails"}
						<FontAwesomeIcon icon={showAllEmails ? faCaretUp : faCaretDown} />
					</button>
					{showAllEmails && (
						<ul className="additional-emails">
							{sortedPhones.slice(1).map((email, index) => (
								<li key={index}>
									<span>{email}</span>
								</li>
							))}
						</ul>
					)}
				</div>
			)}

			{isValidField(data.Website) && (
				<p>
					<strong>Website:</strong> <span dangerouslySetInnerHTML={createMarkup(data.Website)} />
				</p>
			)}

			{sortedAddresses.length > 0 && (
				<p>
					<strong>Address: </strong>
					{isValidAddress(sortedAddresses[currentAddressIndex]) ? (
						<a href={getGoogleMapsLink(sortedAddresses[currentAddressIndex])} target="_blank" rel="noopener noreferrer">
							{sortedAddresses[currentAddressIndex]}
						</a>
					) : (
						<span>{sortedAddresses[currentAddressIndex]}</span>
					)}
				</p>
			)}

			{sortedAddresses.length > 1 && (
				<div>
					<button onClick={toggleAddressVisibility} className="address-toggle-button">
						{showAllAddresses ? "Show Less" : "Show More Addresses"}
						<FontAwesomeIcon icon={showAllAddresses ? faCaretUp : faCaretDown} />
					</button>
					{showAllAddresses && (
						<ul className="additional-addresses">
							{sortedAddresses.slice(1).map((address, index) => (
								<li key={index}>
									{isValidAddress(address) ? (
										<a href={getGoogleMapsLink(address)} target="_blank" rel="noopener noreferrer">
											{address}
										</a>
									) : (
										<span>{address}</span>
									)}
								</li>
							))}
						</ul>
					)}
				</div>
			)}

			{/* Conditionally render the address if valid */}
			{sortedAddresses.length > 0 && showMap && (
				<>
					{/* Render the Google Map if marker location is set */}
					<div className="map-container">
						<APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY} onLoad={() => console.log("Maps API has loaded")}>
							<CustomMap markerLocation={markerLocation} />
						</APIProvider>
					</div>

					<div className="navigation-arrows">
						<button onClick={handlePrevious} disabled={currentAddressIndex === 0}>
							<FontAwesomeIcon icon={faArrowLeft} /> Previous
						</button>
						<button onClick={handleNext} disabled={currentAddressIndex === sortedAddresses.length - 1}>
							Next <FontAwesomeIcon icon={faArrowRight} />
						</button>
					</div>
				</>
			)}

			<p>
				<strong>Rating:</strong>
				<span style={{ display: "inline-block", marginLeft: "8px" }}>{renderStars(data.Rating)}</span>
			</p>
			<button className="navButtons" onClick={() => navigate(-1)}>
				Back to Results
			</button>
		</div>
	);
};

export default Detail;
